import React, { useState, useEffect, useRef } from "react";
import defaultSellerAvatar from "../../assets/profile.png";
import { addToCart } from "../../api/cartApi";
import { getProducts, PRODUCT_SORT_OPTIONS } from "../../api/productApi";
import { CiShop } from "react-icons/ci";
import { IoBagHandleOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import LiveShopColumn from "../marketplace/LiveShopColumn";

import { FaCircle } from "react-icons/fa";
import { IoEyeOutline } from "react-icons/io5";
import { BsFillBagFill } from "react-icons/bs";

const PAGE_SIZE = 20;
const DEFAULT_SORT = PRODUCT_SORT_OPTIONS[0].value;
const initialProducts = [];
const CardList = () => {
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(false);
  const [priceRange, setPriceRange] = useState({ low: 0, high: 0 });
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const priceOptions = [
    { label: "All Prices", low: 0, high: 0 },
    { label: "$0 - $50", low: 0, high: 50 },
    { label: "$50 - $100", low: 50, high: 100 },
    { label: "$100 - $200", low: 100, high: 200 },
    { label: "$200 - $500", low: 200, high: 500 },
  ];
  // Add these near your other state declarations
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [ratingRange, setRatingRange] = useState({ min: 0, max: 5 });

  // Price range and sort/rating are sent together; a newer request replaces an older one still in flight
  const requestIdRef = useRef(0);
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const filters = { sortBy };
    if (priceRange.low > 0) filters.lowestprice = priceRange.low;
    if (priceRange.high > 0) filters.highestprice = priceRange.high;
    if (sortBy === "rating") filters.rating = ratingRange;

    setLoading(true);
    setError(null);
    getProducts(filters, 1, PAGE_SIZE)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        if (res?.success) {
          setProducts((res.result?.products || []).map(mapApiProduct));
        } else {
          setProducts([]);
          setError(res?.message || "Failed to load products");
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setProducts([]);
        setError("Error fetching products");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [priceRange, sortBy, ratingRange]);

  function mapApiProduct(p) {
    const originalPrice = Number(p.product_price) || 0;
    const discountedPrice = Number(p.discount_price) || originalPrice;
    const offerPercentage = originalPrice > discountedPrice
      ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
      : 0;

    return {
      id: p.id,
      name: p.product_name || "Untitled",
      image: p.product_media?.[0]?.url || "/image/shoeimg.png",
      originalPrice: originalPrice.toFixed(2),
      discountedPrice: discountedPrice.toFixed(2),
      offerPercentage,
      sellername: p.seller?.name || "Unknown Seller",
      sellerImage: p.seller?.profile_image?.url || (typeof p.seller?.profile_image === "string" ? p.seller.profile_image : "") || defaultSellerAvatar,
    };
  }

  // 🔹 Handle navigation to ProductView
  
  const handleBuyNow = (productId) => {
   navigate(`/ProductView/${productId}`, {
      state: { productId },
    });
  };

  

  return (
    <div>
      <div className="flex  justify-between items-center mt-10">
        {/* Left: Search */}
        <div className="flex flex-wrap items-center gap-2 w-full">
          <select
            className="border-2 py-1 px-3 rounded-2xl"
            value={priceOptions.findIndex((opt) => opt.low === priceRange.low && opt.high === priceRange.high)}
            onChange={(e) => {
              const selected = priceOptions[e.target.selectedIndex];
              setPriceRange({ low: selected.low, high: selected.high });
            }}
          >
            {priceOptions.map((opt, idx) => (
              <option key={idx} value={idx}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Sort By Select */}
          <select
            className="border-2 py-1 px-3 rounded-2xl"
            value={sortBy}
            onChange={(e) => {
              const newSortBy = e.target.value;
              setSortBy(newSortBy);

              // If rating is selected, show rating range options
              if (newSortBy === "rating") {
                setRatingRange({ min: 3, max: 5 }); // Default rating range
              }
            }}
          >
            {PRODUCT_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Conditional Rating Range Select */}
          {sortBy === "rating" && (
            <select
              className="border-2 py-1 px-3 rounded-2xl"
              value={`${ratingRange.min}-${ratingRange.max}`}
              onChange={(e) => {
                const [min, max] = e.target.value.split("-").map(Number);
                setRatingRange({ min, max });
              }}
            >
              <option value="3-5">3+ Stars</option>
              <option value="4-5">4+ Stars</option>
              <option value="5-5">5 Stars Only</option>
            </select>
          )}
          {(priceRange.high > 0 || sortBy !== DEFAULT_SORT) && (
            <button
              type="button"
              onClick={() => {
                setPriceRange({ low: 0, high: 0 });
                setSortBy(DEFAULT_SORT);
                setRatingRange({ min: 0, max: 5 });
              }}
              className="shrink-0 border-2 border-[#095228] text-[#095228] py-1 px-3 rounded-2xl text-sm font-semibold hover:bg-[#EBF8ED]"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
      <div className="w-full flex flex-col lg:flex-row gap-3">
        <div className="w-full lg:w-[65%]">
          {/* Right: Profile & Cart */}
          {/* <div className="flex items-center gap-4">
            <div className="flex justify-between items-center">
              <button
                  className="flex gap-2  items-center text-white rounded-xl text-xs mt-2 px-5 py-2"
                  style={{
                    background:
                      "linear-gradient(266.95deg, #1DE78B 2.65%, #08AEEA 97.47%)",
                  }}
                >
                  <CiShop className="text-[23px] " />{" "}
                  <span className="text-[18px]">View Seller Profile</span>
                </button>
            </div>
          </div> */}

          <div className="mt-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-3 p-4 text-center">
                  Loading products...
                </div>
              ) : error ? (
                <div className="col-span-3 p-4 text-red-600 text-center">
                  {error}
                </div>
              ) : products.length === 0 ? (
                <div className="col-span-3 p-4 text-center">
                  No products found in this range.
                </div>
              ) : (
                products.map((product) => (
                  <div
                    key={product.id}
                    className="border rounded-md p-2 bg-white flex flex-col shadow-sm hover:shadow-md transition-all"
                  >
                    <div
                      className="bg-[#eceef0] relative rounded-xl cursor-pointer"
                      onClick={() => handleBuyNow(product.id)}
                    >
                      {product.offerPercentage > 0 && (
                        <p className="absolute top-1 left-1 px-3 py-1 rounded-tl-3xl rounded-br-3xl bg-red-700 text-white text-xs font-semibold">
                          {product.offerPercentage}% OFF
                        </p>
                      )}
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-48 object-contain rounded-xl"
                      />
                    </div>
                    {/* ✅ Seller name */}
                    <div className="mt-2">
                      <div className="inline-flex px-2 py-1 rounded-xl gap-1 items-center border-[1px] border-[#08AEEA] ">
                        <img
                          src={product.sellerImage}
                          alt=""
                          className="w-4 h-4 rounded-full object-cover"
                        />
                        <h4 className="text-[10px] font-bold">
                          {product.sellername}
                          {/* {product.id} */}
                        </h4>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <h4 className="text-[13px] font-bold">{product.name}</h4>

                      {product.offerPercentage > 0 && (
                        <p className="text-xs font-semibold line-through text-gray-400">
                          ${product.originalPrice}
                        </p>
                      )}
                      <div className="flex justify-between items-center">
                        <p className="text-[15px] font-bold text-[#095228]">
                          ${product.discountedPrice}
                        </p>
                        {/* <span className="text-xs text-gray-500">
                          {product.offerPercentage}% Offer
                        </span> */}
                        <button
                          className="flex gap-2 items-center bg-[#161F28] text-white rounded-xl text-xs  p-2"
                          onClick={async () => {
                            try {
                              const res = await addToCart(product.id, 1, 0);
                              alert(res.message || "Added to cart");
                              window.location.reload();
                            } catch (err) {
                              alert("Failed to add to cart");
                            }
                          }}
                        >
                          <IoBagHandleOutline className="text-[14px]" />{" "}
                          <span className="text-[10px]">ADD TO CART</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="w-full lg:w-[35%]">
          <LiveShopColumn />
        </div>
      </div>
    </div>
  );
};

export default CardList;
